import { typographyTokens } from '../../../../theme/typographyTokens';
import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Typography, TablePagination, Autocomplete, TextField, Box, useTheme
} from '@mui/material';
import { useGetAdminActivitiesQuery, useGetAdminActivityUsersQuery, useGetAdminActivityTrackableTypesQuery } from '../../../../services/appSlice/appSlice';

type AdminActivity = {
  createdAt: string;
  userID: string;
  title: string;
  description: string;
  trackableType: string;
  action: string;
  property: string;
  oldValue: string;
  newValue: string;
  authorName: string;
};

type AdminActivitiesResponse = {
  count: number;
  activities: AdminActivity[];
};

const AdminActivitiesTable = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedAdmin, setSelectedAdmin] = useState<{ userID: string; authorName: string } | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);

  const { data: adminOptions = [], isLoading: loadingAdmins } = useGetAdminActivityUsersQuery();
  const { data: entityOptions = [], isLoading: loadingEntities } = useGetAdminActivityTrackableTypesQuery();
  const theme = useTheme();

  const {
    data,
    isError: errorAdminActivities,
    isLoading: loadingAdminActivities,
    refetch
  } = useGetAdminActivitiesQuery({
    offset: page * rowsPerPage,
    limit: rowsPerPage,
    userId: selectedAdmin?.userID,
    trackableType: selectedEntity || undefined,
  }) as {
    data: AdminActivitiesResponse | undefined;
    isError: boolean;
    isLoading: boolean;
    refetch: () => void;
  };


  // Track expanded state for each row by index or string key
  const [expandedRows, setExpandedRows] = useState<Record<string | number, boolean>>({});

  if (loadingAdminActivities) {
    return <CircularProgress sx={{ display: 'block', margin: '32px auto' }} />;
  }

  if (errorAdminActivities) {
    return <Typography color="error" align="center">Failed to load admin activities.</Typography>;
  }

  const activities = data?.activities || [];
  const count = data?.count || 0;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleExpand = (idx: number) => {
    setExpandedRows(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3, p: 2 }}>
    <Box
      mb={2}
      p={2}
      sx={{
        borderRadius: 2,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
      }}
    >
      <Autocomplete
        options={adminOptions}
        getOptionLabel={(option) => option.authorName || option.userID}
        loading={loadingAdmins}
        value={selectedAdmin}
        onChange={(_e, value) => {
          setSelectedAdmin(value);
          setPage(0);
        }}
        isOptionEqualToValue={(option, value) => option.userID === value.userID}
        renderInput={(params) => (
          <TextField {...params} label="Admin" variant="outlined" size="small" />
        )}
        sx={{ minWidth: 220 }}
      />
      <Autocomplete
        options={entityOptions}
        getOptionLabel={(option) => option}
        loading={loadingEntities}
        value={selectedEntity}
        onChange={(_e, value) => {
          setSelectedEntity(value);
          setPage(0);
        }}
        renderInput={(params) => (
          <TextField {...params} label="Entity" variant="outlined" size="small" />
        )}
        sx={{ minWidth: 220 }}
      />
    </Box>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 900, background: '#fff' }}>
          <TableHead sx={{ background: '#2372B9' }}>
          <TableRow>
            <TableCell sx={{ color: '#fff', fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.md, letterSpacing: 0.5, py: 2, px: 2 }}>
              Date
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.md, letterSpacing: 0.5, py: 2, px: 2 }}>
              Admin
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.md, letterSpacing: 0.5, py: 2, px: 2 }}>
              Title
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.md, letterSpacing: 0.5, py: 2, px: 2 }}>
              Entity
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.md, letterSpacing: 0.5, py: 2, px: 2 }}>
              Action
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.md, letterSpacing: 0.5, py: 2, px: 2 }}>
              Property
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.md, letterSpacing: 0.5, py: 2, px: 2 }}>
              Old Value
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.md, letterSpacing: 0.5, py: 2, px: 2 }}>
              New Value
            </TableCell>
          </TableRow>
        </TableHead>
          <TableBody>
            {activities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">No admin activities found.</TableCell>
              </TableRow>
            ) : (
              activities.map((row: any, idx: number) => (
                <TableRow
                  key={idx}
                  sx={{
                    '&:hover': { background:  theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f7fa' },
                    borderBottom: '1px solid #e0e0e0',
                    background: theme.palette.mode === 'dark' ? theme.palette.background.default : 'default'
                  }}
                >
                  <TableCell>{new Date(row.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{row.authorName}</TableCell>
                  <TableCell>
                    {row.title && row.title.length > 230 ? (
                      <>
                        {expandedRows[idx] ? row.title : row.title.slice(0, 230) + '...'}{' '}
                        <span
                          style={{ color: '#2372B9', cursor: 'pointer', fontWeight: typographyTokens.fontWeights.medium }}
                          onClick={e => {
                            e.stopPropagation();
                            handleToggleExpand(idx);
                          }}
                        >
                          {expandedRows[idx] ? 'Hide' : 'Show'}
                        </span>
                      </>
                    ) : (
                      row.title
                    )}
                  </TableCell>
                  <TableCell>{row.trackableType}</TableCell>
                  <TableCell>{row.action}</TableCell>
                  <TableCell>{row.property}</TableCell>
                  <TableCell>
                    {row.oldValue && row.oldValue.length > 230 ? (
                      <>
                        {expandedRows['old-' + idx] ? row.oldValue : row.oldValue.slice(0, 230) + '...'}{' '}
                        <span
                          style={{ color: '#2372B9', cursor: 'pointer', fontWeight: typographyTokens.fontWeights.medium }}
                          onClick={e => {
                            e.stopPropagation();
                            setExpandedRows(prev => ({ ...prev, ['old-' + idx]: !prev['old-' + idx] }));
                          }}
                        >
                          {expandedRows['old-' + idx] ? 'Hide' : 'Show'}
                        </span>
                      </>
                    ) : (
                      row.oldValue
                    )}
                  </TableCell>
                  <TableCell>
                    {row.newValue && row.newValue.length > 230 ? (
                      <>
                        {expandedRows['new-' + idx] ? row.newValue : row.newValue.slice(0, 230) + '...'}{' '}
                        <span
                          style={{ color: '#2372B9', cursor: 'pointer', fontWeight: typographyTokens.fontWeights.medium }}
                          onClick={e => {
                            e.stopPropagation();
                            setExpandedRows(prev => ({ ...prev, ['new-' + idx]: !prev['new-' + idx] }));
                          }}
                        >
                          {expandedRows['new-' + idx] ? 'Hide' : 'Show'}
                        </span>
                      </>
                    ) : (
                      row.newValue
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{
          borderTop: '1px solid #e0e0e0',
          background: theme.palette.mode === 'dark' ? theme.palette.background.default : '#fff',
        }}
      />
    </Paper>
  );
};

export default AdminActivitiesTable;